/**
 * Editovatelné info stránky (Robin 26. 10. 2026): když obsluha v appce (Admin → Web · ceny →
 * „Důležité info — celé stránky") vyplní text stránky, zobrazí se místo vestavěné verze.
 * Značky: „## nadpis", „### podnadpis", „- odrážka", „| buňka | buňka |" (tabulka),
 * prázdný řádek = odstavec, **tučně**, odkazy http(s)://, tel: a mailto: se proklikávají.
 * Prázdný slot = vestavěná stránka (děti komponenty).
 */
import type { ReactNode } from 'react'
import { useWebContent } from '../lib/webPrices'

export function PageOverride({ page, title, children }: { page: string; title: string; children: ReactNode }) {
  const content = useWebContent()
  const txt = (content[`page_${page}`] as string | undefined)?.trim()
  if (!txt) return <>{children}</>
  return (
    <section className="bg-paper py-12 md:py-20 min-h-screen">
      <div className="container-x max-w-3xl">
        <p className="h-eyebrow">Důležité info</p>
        <h1 className="h-display text-4xl md:text-5xl mt-3">{title}</h1>
        <div className="mt-8">
          <MarkdownLite text={txt} />
        </div>
        <a href="#top" onClick={() => { window.location.hash = '' }} className="btn-outline-dark mt-10">
          Zpět na hlavní stránku
        </a>
      </div>
    </section>
  )
}

/** Inline: **tučně** + automatické odkazy. */
function inline(s: string, key: string): ReactNode[] {
  const out: ReactNode[] = []
  const re = /(\*\*[^*]+\*\*|https?:\/\/[^\s<)]+|tel:\+?[\d\s]+|mailto:[^\s<)]+|\+420\s?\d{3}\s?\d{3}\s?\d{3})/g
  let last = 0, m: RegExpExecArray | null, i = 0
  while ((m = re.exec(s))) {
    if (m.index > last) out.push(s.slice(last, m.index))
    const t = m[0]
    if (t.startsWith('**')) out.push(<b key={`${key}-${i++}`} className="text-ink-900">{t.slice(2, -2)}</b>)
    else if (t.startsWith('http')) out.push(<a key={`${key}-${i++}`} href={t} target="_blank" rel="noopener" className="text-brand-600 hover:text-brand-700 underline">{t}</a>)
    else if (t.startsWith('tel:') || t.startsWith('+420')) out.push(<a key={`${key}-${i++}`} href={t.startsWith('tel:') ? t.replace(/\s+/g, '') : `tel:${t.replace(/\s+/g, '')}`} className="font-semibold text-ink-900 hover:text-brand-600">{t.replace(/^tel:/, '')}</a>)
    else out.push(<a key={`${key}-${i++}`} href={t} className="font-semibold text-ink-900 hover:text-brand-600">{t.replace(/^mailto:/, '')}</a>)
    last = m.index + t.length
  }
  if (last < s.length) out.push(s.slice(last))
  return out
}

export function MarkdownLite({ text }: { text: string }) {
  const lines = text.replace(/\r/g, '').split('\n')
  const blocks: ReactNode[] = []
  let para: string[] = []
  let list: string[] = []
  let table: string[][] = []
  let k = 0
  const flushPara = () => { if (para.length) { blocks.push(<p key={k++} className="text-[15px] text-ink-800 leading-relaxed">{inline(para.join(' '), `p${k}`)}</p>); para = [] } }
  const flushList = () => { if (list.length) { blocks.push(<ul key={k++} className="list-disc pl-5 space-y-1.5 text-[15px] text-ink-800 leading-relaxed">{list.map((li, j) => <li key={j}>{inline(li, `l${k}-${j}`)}</li>)}</ul>); list = [] } }
  const flushTable = () => {
    if (table.length) {
      blocks.push(
        <div key={k++} className="overflow-x-auto">
          <table className="w-full text-[14px] text-ink-800 border-collapse">
            <tbody>
              {table.map((row, r) => (
                <tr key={r} className="border-b border-ink-900/10">
                  {row.map((c, ci) => <td key={ci} className={`py-2 pr-4 align-top ${r === 0 ? 'font-bold text-ink-900' : ''}`}>{inline(c, `t${k}-${r}-${ci}`)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      )
      table = []
    }
  }
  const flushAll = () => { flushPara(); flushList(); flushTable() }
  for (const raw of lines) {
    const line = raw.trim()
    if (!line) { flushAll(); continue }
    if (line.startsWith('### ')) { flushAll(); blocks.push(<h3 key={k++} className="font-bold text-[16px] text-ink-900 tracking-tight mt-6">{inline(line.slice(4), `h3${k}`)}</h3>); continue }
    if (line.startsWith('## ')) { flushAll(); blocks.push(<h2 key={k++} className="font-bold text-lg text-ink-900 tracking-tight mt-8">{inline(line.slice(3), `h2${k}`)}</h2>); continue }
    if (line.startsWith('# ')) { flushAll(); blocks.push(<h2 key={k++} className="font-bold text-xl text-ink-900 tracking-tight mt-8">{inline(line.slice(2), `h1${k}`)}</h2>); continue }
    if (/^[-•*] /.test(line)) { flushPara(); flushTable(); list.push(line.slice(2)); continue }
    if (line.startsWith('|') && line.endsWith('|')) { flushPara(); flushList(); table.push(line.slice(1, -1).split('|').map((c) => c.trim())); continue }
    flushList(); flushTable(); para.push(line)
  }
  flushAll()
  return <div className="space-y-4">{blocks}</div>
}
